import { lighten } from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useImagePreviewService } from '@onesource/components';
import { getMediaUrl } from '@onesource/services';
import { FC } from 'react';
import { MdZoomIn } from 'react-icons/md';

type Props = {
  label: string;
  approvedVal: string | null;
  pendingVal: string | null;
  fullScreen: boolean;
  width?: number;
  height?: number;
};

const RevisionImg: FC<{ val: string | null; width: number; height: number }> = ({ val, width, height }) => {
  const { openPreview } = useImagePreviewService();

  if (!val) {
    return (
      <Box
        sx={{
          width,
          height,
          borderRadius: 2,
          bgcolor: 'action.hover',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="caption" color="text.disabled">
          No image
        </Typography>
      </Box>
    );
  }

  const resolvedSrc = getMediaUrl(val) || val;
  return (
    <Box
      onClick={() => openPreview(resolvedSrc)}
      sx={{
        position: 'relative',
        width,
        height,
        borderRadius: 2,
        overflow: 'hidden',
        cursor: 'pointer',
        '&:hover .zoom-icon': { opacity: 1 },
      }}
    >
      <Box
        component="img"
        src={resolvedSrc}
        alt={val}
        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
      <Box
        className="zoom-icon"
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          bgcolor: 'rgba(0,0,0,0.45)',
          borderRadius: '50%',
          width: 28,
          height: 28,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: 0,
          transition: 'opacity 0.15s',
          color: '#fff',
        }}
      >
        <MdZoomIn size={18} />
      </Box>
    </Box>
  );
};

export const RevisionImageField: FC<Props> = ({
  label,
  approvedVal,
  pendingVal,
  fullScreen,
  width = 120,
  height = 120,
}) => {
  const changed = JSON.stringify(approvedVal) !== JSON.stringify(pendingVal);

  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        {label}
      </Typography>
      {fullScreen ? (
        <>
          <RevisionImg val={approvedVal} width={width} height={height} />
          {changed && (
            <Box
              sx={{
                mt: 1,
                display: 'inline-block',
                bgcolor: theme => lighten(theme.palette.warning.light, 0.9),
                border: theme => `1px solid ${theme.palette.warning.main}`,
                borderRadius: 2,
                p: 0.5,
              }}
            >
              <RevisionImg val={pendingVal} width={width} height={height} />
            </Box>
          )}
        </>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <RevisionImg val={approvedVal} width={width} height={height} />
          </Grid>
          <Grid item xs={6}>
            {changed ? (
              <Box
                sx={{
                  display: 'inline-block',
                  bgcolor: theme => lighten(theme.palette.warning.light, 0.9),
                  border: theme => `1px solid ${theme.palette.warning.main}`,
                  borderRadius: 2,
                  p: 0.5,
                }}
              >
                <RevisionImg val={pendingVal} width={width} height={height} />
              </Box>
            ) : (
              <RevisionImg val={pendingVal} width={width} height={height} />
            )}
          </Grid>
        </Grid>
      )}
    </Box>
  );
};
